import { EventEmitter } from 'eventemitter3';
import {
    LoggerInput,
    LoggerStatus,
    createLogTraceId,
    logger,
    parseLogError,
} from '@/utils/logger';

const SocketURL = 'wss://cti.jixinyun.com.cn:9991/free';
const CompanyId = 'maimai';
const TAB_OWNER_KEY_PREFIX = 'crm:baichuan:connection-owner:';
const TAB_OWNER_HEARTBEAT_MS = 5000;
const TAB_OWNER_BUSY_TTL_MS = 2 * 60 * 1000;
const RECONNECT_STABLE_MS = 30 * 1000;

export const getBaiChuanTabOwnerKey = (agent: string) => {
    return `${TAB_OWNER_KEY_PREFIX}${encodeURIComponent(agent)}`;
};

export const BaiChuanTimeout = {
    connect: 10000,
    call: 10000,
    // 必须短于 Store 的三次本地挂断兜底总时长，避免旧请求进入下一通电话
    hangup: 2000,
};

interface InitContext {
    token: string;
    agent: string;
    /** 仅用于诊断和测试；生产环境默认自动生成。 */
    tabId?: string;
}

export interface BaiChuanCallContext {
    agent?: string;
    calluuid?: string;
    dateTime?: string;
}

interface PendingRequest<T> {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
    traceId: string;
    startedAt: number;
}

interface TabOwnerRecord {
    tabId: string;
    updatedAt: number;
    busy: boolean;
}

type ClientCloseTrigger =
    | 'connect_timeout'
    | 'socket_error'
    | 'login_error'
    | 'heartbeat_error'
    | 'agent_login_error'
    | 'protocol_error'
    | 'call_timeout'
    | 'call_send_error'
    | 'call_cancelled'
    | 'call_ended_before_uuid'
    | 'hangup_timeout'
    | 'hangup_send_error'
    | 'tab_handoff';

interface ClientCloseIntent {
    trigger: ClientCloseTrigger;
    reconnect: boolean;
    wasConnected: boolean;
}

const getClientCloseStatus = (
    trigger: ClientCloseTrigger,
): Extract<LoggerStatus, 'success' | 'failure' | 'timeout'> => {
    if (trigger === 'tab_handoff' || trigger === 'call_cancelled') {
        return 'success';
    }
    if (
        trigger === 'connect_timeout' ||
        trigger === 'call_timeout' ||
        trigger === 'hangup_timeout'
    ) {
        return 'timeout';
    }
    return 'failure';
};

const createError = (message: string, code?: string) => {
    const error = new Error(message) as Error & { code?: string };
    error.code = code;
    return error;
};

const normalizeError = (error: unknown, fallback: string) => {
    return error instanceof Error ? error : createError(fallback);
};

export class BaiChuanCC extends EventEmitter {
    private webSocket?: WebSocket;
    private token?: string;
    private agent?: string;
    private tabId = createLogTraceId('bc_tab');
    private agentStatusID?: string;

    private tabOwnerKey?: string;
    private ownsConnection = false;
    private tabOwnerHeartbeatTimer?: ReturnType<typeof setInterval>;
    private otherTabBusyExpiryTimer?: ReturnType<typeof setTimeout>;
    private isTabCoordinationListening = false;
    private callIntentActive = false;
    private clientCloseIntents = new WeakMap<WebSocket, ClientCloseIntent>();

    // 上线标志（off：未上线，online：坐席注册，on：坐席上线）
    private onlineflag = 'off';
    private socketOpen = false;
    private shouldReconnect = true;
    private reconnectAttempt = 0;
    private reconnectTimer?: ReturnType<typeof setTimeout>;
    private reconnectStableTimer?: ReturnType<typeof setTimeout>;

    private connectPromise?: Promise<boolean>;
    private connectResolve?: (value: boolean) => void;
    private connectReject?: (error: Error) => void;
    private connectTimer?: ReturnType<typeof setTimeout>;
    private pendingCall?: PendingRequest<BaiChuanCallContext>;
    private pendingHangup?: PendingRequest<boolean>;
    private activeCallUuid?: string;
    private socketTraceId?: string;
    private socketStartedAt = 0;

    private log(input: Omit<LoggerInput, 'module' | 'source'>) {
        logger({
            module: 'call_center',
            source: 'baichuan',
            ...input,
            tags: {
                agentId: this.agent,
                tabId: this.tabId,
                ...input.tags,
            },
        });
    }

    private getStorage() {
        if (typeof window === 'undefined') {
            return undefined;
        }
        try {
            return window.localStorage;
        } catch (error) {
            return undefined;
        }
    }

    private readTabOwner(): TabOwnerRecord | undefined {
        const storage = this.getStorage();
        if (!storage || !this.tabOwnerKey) {
            return undefined;
        }
        try {
            const raw = storage.getItem(this.tabOwnerKey);
            if (!raw) {
                return undefined;
            }
            const parsed = JSON.parse(raw) as Partial<TabOwnerRecord>;
            if (
                typeof parsed.tabId !== 'string' ||
                typeof parsed.updatedAt !== 'number' ||
                typeof parsed.busy !== 'boolean'
            ) {
                return undefined;
            }
            return parsed as TabOwnerRecord;
        } catch (error) {
            return undefined;
        }
    }

    private isBusy() {
        return !!(
            this.callIntentActive ||
            this.pendingCall ||
            this.pendingHangup ||
            this.activeCallUuid
        );
    }

    private hasEstablishedCall() {
        return !!(
            this.pendingCall ||
            this.pendingHangup ||
            this.activeCallUuid
        );
    }

    private clearOtherTabBusyExpiryTimer() {
        if (this.otherTabBusyExpiryTimer) {
            clearTimeout(this.otherTabBusyExpiryTimer);
            this.otherTabBusyExpiryTimer = undefined;
        }
    }

    private syncOtherTabBusy(owner = this.readTabOwner()) {
        this.clearOtherTabBusyExpiryTimer();
        const expiresIn = owner
            ? TAB_OWNER_BUSY_TTL_MS - (Date.now() - owner.updatedAt)
            : 0;
        const otherTabBusy = !!(
            owner &&
            owner.tabId !== this.tabId &&
            owner.busy &&
            expiresIn > 0
        );
        this.emit('otherTabBusyChange', otherTabBusy);
        if (otherTabBusy) {
            this.otherTabBusyExpiryTimer = setTimeout(() => {
                this.otherTabBusyExpiryTimer = undefined;
                const latestOwner = this.readTabOwner();
                const hasRenewed = !!(
                    latestOwner &&
                    latestOwner.tabId !== this.tabId &&
                    latestOwner.busy &&
                    (latestOwner.tabId !== owner!.tabId ||
                        latestOwner.updatedAt > owner!.updatedAt)
                );
                if (hasRenewed) {
                    this.syncOtherTabBusy(latestOwner);
                } else {
                    this.emit('otherTabBusyChange', false);
                }
            }, expiresIn + 1);
        }
    }

    private writeTabOwner() {
        const storage = this.getStorage();
        if (!storage || !this.tabOwnerKey) {
            this.ownsConnection = true;
            this.syncOtherTabBusy();
            return true;
        }
        try {
            storage.setItem(
                this.tabOwnerKey,
                JSON.stringify({
                    tabId: this.tabId,
                    updatedAt: Date.now(),
                    busy: this.isBusy(),
                } as TabOwnerRecord),
            );
            const owner = this.readTabOwner();
            this.ownsConnection = owner?.tabId === this.tabId;
            if (this.ownsConnection) {
                this.startTabOwnerHeartbeat();
            }
            return this.ownsConnection;
        } catch (error) {
            // localStorage 不可用时退化为单页行为，不能阻断电话功能。
            this.ownsConnection = true;
            this.syncOtherTabBusy();
            return true;
        }
    }

    private refreshTabOwner() {
        if (!this.ownsConnection) {
            return;
        }
        const owner = this.readTabOwner();
        if (!owner || owner.tabId === this.tabId) {
            this.writeTabOwner();
        }
    }

    private claimConnectionOwnership() {
        const storage = this.getStorage();
        if (!storage || !this.tabOwnerKey) {
            this.ownsConnection = true;
            this.syncOtherTabBusy();
            return true;
        }

        const currentOwner = this.readTabOwner();
        if (currentOwner?.tabId === this.tabId) {
            this.ownsConnection = true;
            this.writeTabOwner();
            this.syncOtherTabBusy();
            return true;
        }

        const ownerIsBusy =
            !!currentOwner?.busy &&
            Date.now() - currentOwner.updatedAt < TAB_OWNER_BUSY_TTL_MS;
        if (ownerIsBusy) {
            this.syncOtherTabBusy(currentOwner);
            this.log({
                action: 'tab_ownership',
                level: 'warn',
                status: 'failure',
                errorCode: 'TAB_BUSY_IN_OTHER_PAGE',
                errorMessage: '其他 CRM 页面正在使用电话服务',
                tags: {
                    ownershipResult: 'blocked',
                    otherTabBusy: true,
                },
            });
            return false;
        }

        const previousOwnerPresent = !!currentOwner;
        const acquired = this.writeTabOwner();
        if (acquired) {
            this.syncOtherTabBusy();
            this.log({
                action: 'tab_ownership',
                status: 'success',
                tags: {
                    ownershipResult: 'acquired',
                    previousOwnerPresent,
                },
            });
        }
        return acquired;
    }

    private ownsConnectionForReconnect() {
        const storage = this.getStorage();
        if (!storage || !this.tabOwnerKey) {
            return true;
        }
        const owner = this.readTabOwner();
        if (owner?.tabId === this.tabId) {
            this.ownsConnection = true;
            return true;
        }
        if (!owner && this.ownsConnection) {
            return this.writeTabOwner();
        }
        this.ownsConnection = false;
        this.stopTabOwnerHeartbeat();
        return false;
    }

    private startTabOwnerHeartbeat() {
        if (this.tabOwnerHeartbeatTimer || !this.getStorage()) {
            return;
        }
        this.tabOwnerHeartbeatTimer = setInterval(() => {
            const owner = this.readTabOwner();
            if (owner && owner.tabId !== this.tabId) {
                this.handleOwnershipTaken();
                return;
            }
            if (this.ownsConnection) {
                this.writeTabOwner();
            }
        }, TAB_OWNER_HEARTBEAT_MS);
    }

    private stopTabOwnerHeartbeat() {
        if (this.tabOwnerHeartbeatTimer) {
            clearInterval(this.tabOwnerHeartbeatTimer);
            this.tabOwnerHeartbeatTimer = undefined;
        }
    }

    private releaseTabOwnership() {
        const storage = this.getStorage();
        if (storage && this.tabOwnerKey) {
            try {
                if (this.readTabOwner()?.tabId === this.tabId) {
                    storage.removeItem(this.tabOwnerKey);
                }
            } catch (error) {
                // 页面退出时释放失败不应影响卸载流程。
            }
        }
        this.ownsConnection = false;
        this.stopTabOwnerHeartbeat();
    }

    private handleOwnershipTaken = () => {
        if (!this.ownsConnection) {
            return;
        }
        const currentOwner = this.readTabOwner();
        if (!currentOwner || currentOwner.tabId === this.tabId) {
            return;
        }

        const retainReason = this.hasEstablishedCall()
            ? 'active_call'
            : this.callIntentActive &&
              currentOwner.busy &&
              this.tabId < currentOwner.tabId
            ? 'concurrent_claim_priority'
            : undefined;
        if (retainReason) {
            // 已建立的通话始终优先；同时拨号时按 tabId 确定唯一胜者，避免双方反复写回。
            this.writeTabOwner();
            this.syncOtherTabBusy();
            this.log({
                action: 'tab_ownership',
                level: 'warn',
                status: 'retry',
                tags: {
                    ownershipResult: 'retained',
                    ownershipReason: retainReason,
                },
            });
            return;
        }

        this.ownsConnection = false;
        this.syncOtherTabBusy(currentOwner);
        this.shouldReconnect = false;
        this.stopTabOwnerHeartbeat();
        this.clearReconnectTimer();
        this.clearReconnectStableTimer();
        this.log({
            action: 'tab_ownership',
            status: 'success',
            tags: {
                ownershipResult: 'released',
                ownershipReason: 'other_tab',
            },
        });

        const error = createError(
            '电话服务已切换到其他 CRM 页面',
            'TAB_HANDOFF',
        );
        const webSocket = this.webSocket;
        if (webSocket) {
            this.requestSocketClose(webSocket, 'tab_handoff', false);
        }
        this.handleSocketFailure(error);
        this.emit('disconnect', { reason: 'tab_handoff' });
    };

    private handleStorageChange = (event: StorageEvent) => {
        if (!this.tabOwnerKey || event.key !== this.tabOwnerKey) {
            return;
        }
        const owner = this.readTabOwner();
        if (owner && owner.tabId !== this.tabId) {
            if (this.ownsConnection) {
                this.handleOwnershipTaken();
            } else {
                this.syncOtherTabBusy(owner);
            }
            return;
        }
        this.syncOtherTabBusy(owner);
    };

    private handleBeforeUnload = () => {
        this.shouldReconnect = false;
        this.releaseTabOwnership();
    };

    private setupTabCoordination() {
        if (this.isTabCoordinationListening || typeof window === 'undefined') {
            return;
        }
        window.addEventListener('storage', this.handleStorageChange);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        this.isTabCoordinationListening = true;
    }

    private logError(
        action: string,
        traceId: string | undefined,
        startedAt: number,
        error: Error,
        tags?: LoggerInput['tags'],
    ) {
        const { errorCode, errorMessage } = parseLogError(error);
        const isTimeout =
            String(errorCode || '').includes('TIMEOUT') ||
            errorMessage.includes('超时');
        this.log({
            action,
            level: 'error',
            status: isTimeout ? 'timeout' : 'failure',
            traceId,
            costMs: startedAt ? Date.now() - startedAt : undefined,
            errorCode,
            errorMessage,
            tags,
        });
    }

    public init(context: InitContext) {
        const nextTabId = context.tabId || this.tabId;
        if (
            (this.agent && this.agent !== context.agent) ||
            this.tabId !== nextTabId
        ) {
            this.releaseTabOwnership();
        }
        this.token = context.token;
        this.agent = context.agent;
        this.tabId = nextTabId;
        this.tabOwnerKey = getBaiChuanTabOwnerKey(context.agent);
        this.setupTabCoordination();
        this.syncOtherTabBusy();
        this.log({
            action: 'sdk_init',
            status: 'success',
        });
    }

    public connect(): Promise<boolean> {
        return this.connectInternal(true);
    }

    private connectInternal(takeOwnership: boolean): Promise<boolean> {
        const hasOwnership = takeOwnership
            ? this.claimConnectionOwnership()
            : this.ownsConnectionForReconnect();
        if (!hasOwnership) {
            return Promise.reject(
                createError(
                    '其他 CRM 页面正在使用电话服务，请在原页面结束通话后重试',
                    'TAB_BUSY_IN_OTHER_PAGE',
                ),
            );
        }
        this.shouldReconnect = true;

        if (
            this.socketOpen &&
            this.webSocket?.readyState === WebSocket.OPEN &&
            this.onlineflag === 'on'
        ) {
            return Promise.resolve(true);
        }
        if (this.connectPromise) {
            return this.connectPromise;
        }
        if (!this.token || !this.agent) {
            const error = createError(
                '百川 SDK 未初始化',
                'SDK_NOT_INITIALIZED',
            );
            this.logError('ws_connect', undefined, Date.now(), error);
            return Promise.reject(error);
        }

        this.clearReconnectTimer();
        this.socketTraceId = createLogTraceId('bc_ws');
        this.socketStartedAt = Date.now();
        this.log({
            action: 'ws_connect',
            status: 'start',
            traceId: this.socketTraceId,
            tags: {
                reconnectAttempt: this.reconnectAttempt,
            },
        });

        const connectPromise = new Promise<boolean>((resolve, reject) => {
            this.connectResolve = resolve;
            this.connectReject = reject;
        });
        this.connectPromise = connectPromise;

        let webSocket: WebSocket;
        try {
            webSocket = new WebSocket(SocketURL);
        } catch (error) {
            const normalizedError = normalizeError(
                error,
                '百川服务器连接创建失败',
            );
            (normalizedError as Error & { code?: string }).code =
                'WS_CREATE_FAILED';
            this.rejectConnect(normalizedError);
            this.scheduleReconnect();
            return connectPromise;
        }

        this.webSocket = webSocket;
        this.agentStatusID = undefined;
        this.connectTimer = setTimeout(() => {
            if (this.webSocket !== webSocket) {
                return;
            }
            const error = createError(
                '百川服务器连接超时，请稍后重试',
                'WS_CONNECT_TIMEOUT',
            );
            this.rejectConnect(error);
            this.requestSocketClose(webSocket, 'connect_timeout');
        }, BaiChuanTimeout.connect);

        webSocket.onerror = () => {
            if (this.webSocket !== webSocket) {
                return;
            }
            const error = createError(
                '百川服务器连接异常，请稍后重试',
                'WS_ERROR',
            );
            this.logError(
                'ws_error',
                this.socketTraceId,
                this.socketStartedAt,
                error,
                { readyState: webSocket.readyState },
            );
            this.requestSocketClose(webSocket, 'socket_error');
            this.handleSocketFailure(error);
        };
        webSocket.onopen = () => {
            if (this.webSocket !== webSocket) {
                return;
            }
            this.socketOpen = true;
            this.onlineflag = 'off';
            this.log({
                action: 'ws_open',
                status: 'success',
                traceId: this.socketTraceId,
                costMs: Date.now() - this.socketStartedAt,
            });
            try {
                this.online();
            } catch (error) {
                this.requestSocketClose(webSocket, 'login_error');
                this.handleSocketFailure(
                    normalizeError(error, '百川坐席注册失败'),
                );
            }
        };
        webSocket.onclose = event => {
            if (this.webSocket !== webSocket) {
                return;
            }
            const closeIntent = this.clientCloseIntents.get(webSocket);
            this.clientCloseIntents.delete(webSocket);
            const wasConnected = closeIntent?.wasConnected ?? this.socketOpen;
            const reason = event.reason ? `：${event.reason}` : '';
            const error = createError(
                `百川服务器连接已断开（${event.code}）${reason}`,
                `WS_CLOSED_${event.code}`,
            );

            if (closeIntent) {
                const closeStatus = getClientCloseStatus(closeIntent.trigger);
                this.log({
                    action: 'ws_close',
                    level: closeStatus === 'success' ? 'info' : 'error',
                    status: closeStatus,
                    traceId: this.socketTraceId,
                    costMs: Date.now() - this.socketStartedAt,
                    tags: {
                        closeOrigin: 'client',
                        closeTrigger: closeIntent.trigger,
                        closeCode: event.code,
                        closeReason: event.reason,
                        wasConnected,
                        reconnectPlanned: closeIntent.reconnect,
                    },
                });
            } else {
                const reconnectPlanned = event.code !== 1000;
                this.logError(
                    'ws_close',
                    this.socketTraceId,
                    this.socketStartedAt,
                    error,
                    {
                        closeOrigin: 'remote',
                        closeTrigger: 'unexpected',
                        closeCode: event.code,
                        closeReason: event.reason,
                        wasConnected,
                        reconnectPlanned,
                    },
                );
            }

            this.webSocket = undefined;
            this.socketOpen = false;
            this.onlineflag = 'off';
            this.agentStatusID = undefined;
            this.handleSocketFailure(error);

            // 远端 1000 常见于同坐席新连接挤掉旧连接。后台自动抢回只会形成
            // 多页面互踢；停止重试后，下一次用户拨号仍会显式重新连接。
            const shouldReconnect = closeIntent
                ? closeIntent.reconnect
                : event.code !== 1000;
            if (!shouldReconnect) {
                this.releaseTabOwnership();
            }
            if (!closeIntent || closeIntent.reconnect) {
                this.emit('disconnect', event);
            }
            if (shouldReconnect && this.ownsConnectionForReconnect()) {
                this.scheduleReconnect();

                if (wasConnected) {
                    this.emit('reconnecting');
                }
            }
        };
        webSocket.onmessage = event => {
            if (this.webSocket === webSocket) {
                this.receive(event);
            }
        };

        return connectPromise;
    }

    private clearConnectTask() {
        if (this.connectTimer) {
            clearTimeout(this.connectTimer);
            this.connectTimer = undefined;
        }
        this.connectPromise = undefined;
        this.connectResolve = undefined;
        this.connectReject = undefined;
    }

    private resolveConnect(value: boolean) {
        const resolve = this.connectResolve;
        if (resolve) {
            this.log({
                action: 'ws_connect',
                status: 'success',
                traceId: this.socketTraceId,
                costMs: Date.now() - this.socketStartedAt,
            });
        }
        this.clearConnectTask();
        this.scheduleReconnectAttemptReset();
        resolve?.(value);
    }

    private rejectConnect(error: Error) {
        const reject = this.connectReject;
        if (reject) {
            this.logError(
                'ws_connect',
                this.socketTraceId,
                this.socketStartedAt,
                error,
            );
        }
        this.clearConnectTask();
        reject?.(error);
    }

    private clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
    }

    private clearReconnectStableTimer() {
        if (this.reconnectStableTimer) {
            clearTimeout(this.reconnectStableTimer);
            this.reconnectStableTimer = undefined;
        }
    }

    private scheduleReconnectAttemptReset() {
        this.clearReconnectStableTimer();
        this.reconnectStableTimer = setTimeout(() => {
            this.reconnectStableTimer = undefined;
            if (
                this.socketOpen &&
                this.webSocket?.readyState === WebSocket.OPEN &&
                this.onlineflag === 'on'
            ) {
                this.reconnectAttempt = 0;
            }
        }, RECONNECT_STABLE_MS);
    }

    private scheduleReconnect() {
        if (
            !this.shouldReconnect ||
            this.reconnectTimer ||
            !this.token ||
            !this.agent ||
            !this.ownsConnectionForReconnect()
        ) {
            return;
        }

        const delay = Math.min(
            1000 * Math.pow(2, this.reconnectAttempt),
            30000,
        );
        this.reconnectAttempt += 1;
        this.log({
            action: 'ws_reconnect',
            level: 'warn',
            status: 'retry',
            traceId: this.socketTraceId,
            tags: {
                reconnectAttempt: this.reconnectAttempt,
            },
            metrics: {
                retryDelayMs: delay,
            },
        });
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;
            this.connectInternal(false).catch(() => undefined);
        }, delay);
    }

    private requestSocketClose(
        webSocket: WebSocket,
        trigger: ClientCloseTrigger,
        reconnect = true,
    ) {
        if (
            webSocket.readyState === WebSocket.CONNECTING ||
            webSocket.readyState === WebSocket.OPEN
        ) {
            this.clientCloseIntents.set(webSocket, {
                trigger,
                reconnect,
                wasConnected: this.socketOpen,
            });
            try {
                webSocket.close(1000, trigger);
            } catch (error) {
                this.clientCloseIntents.delete(webSocket);
                // 关闭失败时仍由重连定时器恢复连接
            }
        }
        if (reconnect) {
            this.scheduleReconnect();
        }
    }

    private reconnect(trigger: ClientCloseTrigger) {
        const webSocket = this.webSocket;
        this.onlineflag = 'off';
        this.agentStatusID = undefined;
        this.clearReconnectStableTimer();

        if (webSocket) {
            this.requestSocketClose(webSocket, trigger);
            this.socketOpen = false;
        } else {
            this.socketOpen = false;
            this.scheduleReconnect();
        }
    }

    private rotateSocket(trigger: ClientCloseTrigger) {
        const webSocket = this.webSocket;
        const wasConnected = this.socketOpen;
        this.webSocket = undefined;
        this.socketOpen = false;
        this.onlineflag = 'off';
        this.agentStatusID = undefined;

        if (
            webSocket &&
            (webSocket.readyState === WebSocket.CONNECTING ||
                webSocket.readyState === WebSocket.OPEN)
        ) {
            const closeStatus = getClientCloseStatus(trigger);
            this.log({
                action: 'ws_close',
                level: closeStatus === 'success' ? 'info' : 'error',
                status: closeStatus,
                traceId: this.socketTraceId,
                costMs: Date.now() - this.socketStartedAt,
                tags: {
                    closeOrigin: 'client',
                    closeTrigger: trigger,
                    closeCode: 1000,
                    wasConnected,
                    reconnectPlanned: true,
                },
            });
            try {
                this.clientCloseIntents.set(webSocket, {
                    trigger,
                    reconnect: true,
                    wasConnected,
                });
                webSocket.close(1000, trigger);
            } catch (error) {
                // 即使旧连接关闭失败，也已通过实例校验隔离它后续的事件
            }
        }
        this.scheduleReconnect();
    }

    private send(params: (string | number | undefined)[]) {
        const webSocket = this.webSocket;
        if (!webSocket) {
            throw createError('百川服务器未连接', 'WS_NOT_CONNECTED');
        }
        if (webSocket.readyState !== WebSocket.OPEN) {
            throw createError(
                `百川服务器连接不可用（readyState=${webSocket.readyState}）`,
                'WS_NOT_OPEN',
            );
        }
        webSocket.send(params.join('|'));
    }

    private receive(message: MessageEvent) {
        const allState = message.data;
        if (!allState) {
            return;
        }

        const superArray = String(allState)
            .split(/[\r\n]+/)
            .filter(Boolean);

        superArray.forEach(item => {
            const stateArray = item.includes('|') ? item.split('|') : [item];
            const action = stateArray[0];

            if (/^0[1-8]$/.test(action)) {
                this.handleError(item);
                return;
            }

            const actions: { [key: string]: (msg: string) => void } = {
                doLoginOK: () => this.handleDoLoginOK(),
                HeartBeat: () => this.heartbeat(),
                AgentStatus: state => this.handleAgentStatus(state),
                AgentCallbackLoginOK: () => this.handleAgentCallbackLoginOK(),
                AgentLogoffOK: () => this.handleAgentLogout(),
                NewState: state => this.handleNewState(state),
                CallUuid: state => this.handleCall(state),
            };

            actions[action]?.(item);
        });
    }

    private heartbeat() {
        try {
            this.send(['HeartBeat']);
        } catch (error) {
            const normalizedError = normalizeError(error, '百川心跳发送失败');
            this.logError(
                'ws_heartbeat',
                this.socketTraceId,
                this.socketStartedAt,
                normalizedError,
            );
            this.handleSocketFailure(normalizedError);
            this.reconnect('heartbeat_error');
        }
    }

    private handleSocketFailure(error: Error) {
        this.socketOpen = false;
        this.onlineflag = 'off';
        this.activeCallUuid = undefined;
        this.callIntentActive = false;
        this.clearReconnectStableTimer();
        this.rejectConnect(error);
        this.rejectPendingCall(error);
        this.rejectPendingHangup(error);
        this.refreshTabOwner();
    }

    private handleAgentLogout() {
        this.onlineflag = 'off';
        try {
            this.send(['doLoginOff']);
        } catch (error) {
            this.handleSocketFailure(normalizeError(error, '百川坐席下线失败'));
        }
    }

    private handleError(state: string) {
        const code = state.split('|')[0];
        const wasConnecting = !!this.connectPromise;
        const errorMessages: { [key: string]: string } = {
            '01': '请求失败！参数有误！',
            '02': '请求失败！此坐席已经在其他地方注册！请稍后再试!',
            '03': '请求失败！请先进行请求注册!',
            '04': '请求失败！无效坐席!',
            '05': '请求失败！百川 token 已过期，请刷新页面!',
            '06': '请求失败！超出并发限制!',
            '07': '请求失败！分机不存在!',
            '08': '请求失败！分机已被占用!',
        };
        const error = createError(
            errorMessages[code] || `百川请求失败（${code}）`,
            code,
        );

        this.logError(
            'protocol_error',
            this.socketTraceId,
            this.socketStartedAt,
            error,
            { vendorCode: code },
        );

        this.rejectConnect(error);
        this.rejectPendingCall(error);
        this.rejectPendingHangup(error);

        // 登录阶段的协议错误不能遗留一条仍存活的旧连接，否则下次重试会并存多个会话
        if (wasConnecting && this.webSocket) {
            this.shouldReconnect = false;
            this.requestSocketClose(this.webSocket, 'protocol_error', false);
        }
    }

    private handleDoLoginOK() {
        this.emit('doLoginOK');
        this.onlineflag = 'online';
        try {
            this.send(['AgentStatus', this.agent]);
        } catch (error) {
            this.handleSocketFailure(
                normalizeError(error, '百川坐席状态查询失败'),
            );
            this.reconnect('agent_login_error');
        }
    }

    private handleAgentCallbackLoginOK() {
        this.emit('agentCallbackLoginOK');
        this.onlineflag = 'on';
        this.resolveConnect(true);
    }

    private online() {
        this.send([
            'doLogin_free',
            this.agent,
            this.token,
            this.agent,
            'sip',
            CompanyId,
        ]);
    }

    private agentLogin() {
        this.send([
            'AgentCallbackLogin',
            'agent-login',
            this.agent,
            this.agent,
        ]);
    }

    private handleAgentStatus(state: string) {
        const stateArray = state.split('|');
        if (stateArray.length <= 4) {
            return;
        }

        const nextAgentStatusID = stateArray[4];
        const loginStatus = stateArray[2];

        if (loginStatus === '1') {
            this.agentStatusID = nextAgentStatusID;
            this.onlineflag = 'on';
            this.resolveConnect(true);
            return;
        }

        if (this.agentStatusID === nextAgentStatusID) {
            return;
        }

        this.agentStatusID = nextAgentStatusID;
        try {
            this.agentLogin();
        } catch (error) {
            this.handleSocketFailure(normalizeError(error, '百川坐席登录失败'));
            this.reconnect('agent_login_error');
        }
    }

    public async call(
        callNumber: string | number,
    ): Promise<BaiChuanCallContext> {
        const traceId = createLogTraceId('bc_call');
        const startedAt = Date.now();
        this.callIntentActive = true;
        this.log({
            action: 'call_request',
            status: 'start',
            traceId,
        });

        try {
            await this.connect();

            if (this.pendingCall) {
                throw createError(
                    '已有正在处理的百川拨号请求',
                    'CALL_IN_PROGRESS',
                );
            }
        } catch (error) {
            this.callIntentActive = false;
            this.refreshTabOwner();
            const normalizedError = normalizeError(error, '百川拨号准备失败');
            this.logError('call_request', traceId, startedAt, normalizedError);
            throw normalizedError;
        }

        let resolveRequest!: (value: BaiChuanCallContext) => void;
        let rejectRequest!: (error: Error) => void;
        const promise = new Promise<BaiChuanCallContext>((resolve, reject) => {
            resolveRequest = resolve;
            rejectRequest = reject;
        });
        const timer = setTimeout(() => {
            this.rejectPendingCall(
                createError(
                    '拨号请求超时，电话服务正在自动重连',
                    'CALL_UUID_TIMEOUT',
                ),
            );
            this.reconnect('call_timeout');
        }, BaiChuanTimeout.call);

        this.pendingCall = {
            promise,
            resolve: resolveRequest,
            reject: rejectRequest,
            timer,
            traceId,
            startedAt,
        };
        this.refreshTabOwner();

        try {
            this.send([
                'MakeCall',
                0,
                this.agent,
                callNumber,
                0,
                callNumber,
                ,
                ,
                ,
                ,
                0,
            ]);
        } catch (error) {
            const normalizedError = normalizeError(error, '百川拨号发送失败');
            this.rejectPendingCall(normalizedError);
            this.reconnect('call_send_error');
        }

        return promise;
    }

    private resolvePendingCall(context: BaiChuanCallContext) {
        const pendingCall = this.pendingCall;
        if (!pendingCall) {
            return;
        }
        clearTimeout(pendingCall.timer);
        this.pendingCall = undefined;
        this.callIntentActive = false;
        this.activeCallUuid = context.calluuid;
        this.refreshTabOwner();
        this.log({
            action: 'call_request',
            status: 'success',
            traceId: pendingCall.traceId,
            costMs: Date.now() - pendingCall.startedAt,
            tags: {
                callUuid: context.calluuid,
            },
        });
        pendingCall.resolve(context);
    }

    private rejectPendingCall(error: Error) {
        const pendingCall = this.pendingCall;
        if (!pendingCall) {
            return;
        }
        clearTimeout(pendingCall.timer);
        this.pendingCall = undefined;
        this.callIntentActive = false;
        this.refreshTabOwner();
        this.logError(
            'call_request',
            pendingCall.traceId,
            pendingCall.startedAt,
            error,
        );
        pendingCall.reject(error);
    }

    private handleCall(state: string) {
        const stateArray = state.split('|');
        const callContext = {
            agent: stateArray[1],
            calluuid: stateArray[2],
            dateTime: stateArray[7],
        };
        if (!this.pendingCall) {
            this.log({
                action: 'call_uuid_orphan',
                level: 'warn',
                status: 'failure',
                traceId: createLogTraceId('bc_call_orphan'),
                tags: {
                    callUuid: callContext.calluuid,
                },
            });
        }
        this.resolvePendingCall(callContext);
        this.emit('callUuid', callContext);
    }

    private handleNewState(allState: string) {
        const stateArray = allState?.split('|') || [];
        const state = stateArray[2];
        const legNumber = stateArray[3];
        const stateActionMap: { [key: string]: () => void } = {
            Ringing: () => this.emit('ringing'),
            Up: () => {
                if (legNumber === '1') {
                    this.emit('pickUp');
                }
            },
            Ring: () => this.emit('ring'),
            Hangup: () => {
                if (
                    this.pendingCall &&
                    !this.pendingHangup &&
                    !this.activeCallUuid
                ) {
                    this.rejectPendingCall(
                        createError(
                            '拨号在建立通话前已结束',
                            'CALL_ENDED_BEFORE_UUID',
                        ),
                    );
                    // 无 UUID 时无法区分迟到事件，轮换连接可隔离后续旧 CallUuid
                    this.rotateSocket('call_ended_before_uuid');
                    this.emit('hangup');
                    return;
                }
                if (!this.pendingHangup && !this.activeCallUuid) {
                    this.log({
                        action: 'hangup_orphan',
                        level: 'warn',
                        status: 'failure',
                        traceId: createLogTraceId('bc_hangup_orphan'),
                        tags: {
                            hasPendingCall: !!this.pendingCall,
                        },
                    });
                    return;
                }
                this.resolvePendingHangup(true);
                this.activeCallUuid = undefined;
                this.refreshTabOwner();
                this.emit('hangup');
            },
        };

        stateActionMap[state]?.();
    }

    public hangup(): Promise<boolean> {
        if (this.pendingHangup) {
            return this.pendingHangup.promise;
        }

        const traceId = createLogTraceId('bc_hangup');
        const startedAt = Date.now();
        this.log({
            action: 'hangup_request',
            status: 'start',
            traceId,
        });

        // CallUuid 尚未返回时挂断，必须隔离旧连接，防止迟到的 CallUuid 命中下一次拨号
        if (this.pendingCall) {
            this.rejectPendingCall(createError('拨号已取消', 'CALL_CANCELLED'));
            try {
                this.send(['Hangup']);
                this.log({
                    action: 'hangup_request',
                    status: 'success',
                    traceId,
                    costMs: Date.now() - startedAt,
                    tags: {
                        connectionRotated: true,
                    },
                });
                this.rotateSocket('call_cancelled');
                return Promise.resolve(true);
            } catch (error) {
                const normalizedError = normalizeError(
                    error,
                    '百川挂断发送失败',
                );
                this.logError(
                    'hangup_request',
                    traceId,
                    startedAt,
                    normalizedError,
                );
                this.rotateSocket('hangup_send_error');
                return Promise.reject(normalizedError);
            }
        }

        let resolveRequest!: (value: boolean) => void;
        let rejectRequest!: (error: Error) => void;
        const promise = new Promise<boolean>((resolve, reject) => {
            resolveRequest = resolve;
            rejectRequest = reject;
        });
        const timer = setTimeout(() => {
            this.rejectPendingHangup(
                createError('挂断请求超时', 'HANGUP_TIMEOUT'),
            );
            this.reconnect('hangup_timeout');
        }, BaiChuanTimeout.hangup);

        this.pendingHangup = {
            promise,
            resolve: resolveRequest,
            reject: rejectRequest,
            timer,
            traceId,
            startedAt,
        };
        this.refreshTabOwner();

        try {
            this.send(['Hangup']);
        } catch (error) {
            const normalizedError = normalizeError(error, '百川挂断发送失败');
            this.rejectPendingHangup(normalizedError);
            this.reconnect('hangup_send_error');
        }

        return promise;
    }

    private resolvePendingHangup(value: boolean) {
        const pendingHangup = this.pendingHangup;
        if (!pendingHangup) {
            return;
        }
        clearTimeout(pendingHangup.timer);
        this.pendingHangup = undefined;
        this.activeCallUuid = undefined;
        this.refreshTabOwner();
        this.log({
            action: 'hangup_request',
            status: 'success',
            traceId: pendingHangup.traceId,
            costMs: Date.now() - pendingHangup.startedAt,
        });
        pendingHangup.resolve(value);
    }

    private rejectPendingHangup(error: Error) {
        const pendingHangup = this.pendingHangup;
        if (!pendingHangup) {
            return;
        }
        clearTimeout(pendingHangup.timer);
        this.pendingHangup = undefined;
        this.refreshTabOwner();
        this.logError(
            'hangup_request',
            pendingHangup.traceId,
            pendingHangup.startedAt,
            error,
        );
        pendingHangup.reject(error);
    }

    public logout() {
        this.shouldReconnect = false;
        this.clearReconnectTimer();
        if (this.onlineflag === 'online') {
            this.handleAgentLogout();
        } else {
            this.send(['AgentLogoff', this.agent]);
        }
    }

    public dispose() {
        this.shouldReconnect = false;
        this.clearReconnectTimer();
        this.clearReconnectStableTimer();
        this.clearOtherTabBusyExpiryTimer();
        this.handleSocketFailure(
            createError('百川 SDK 已销毁', 'SDK_DISPOSED'),
        );
        this.agentStatusID = undefined;
        this.reconnectAttempt = 0;
        this.releaseTabOwnership();
        if (this.isTabCoordinationListening && typeof window !== 'undefined') {
            window.removeEventListener('storage', this.handleStorageChange);
            window.removeEventListener('beforeunload', this.handleBeforeUnload);
            this.isTabCoordinationListening = false;
        }

        const webSocket = this.webSocket;
        this.webSocket = undefined;
        this.clientCloseIntents = new WeakMap<WebSocket, ClientCloseIntent>();
        if (webSocket) {
            webSocket.onopen = null;
            webSocket.onclose = null;
            webSocket.onerror = null;
            webSocket.onmessage = null;
            if (
                webSocket.readyState === WebSocket.CONNECTING ||
                webSocket.readyState === WebSocket.OPEN
            ) {
                try {
                    webSocket.close(1000, 'tab_handoff');
                } catch (error) {
                    // 测试销毁或页面卸载阶段忽略关闭异常。
                }
            }
        }
        this.removeAllListeners();
    }
}

export default new BaiChuanCC();
